import React, { useContext, useEffect, useState } from 'react';
import ConfirmDialogTemplate from '@/Components/Templates/ConfirmDialogTemplate';
import { fieldsPlaceholders } from '@/Config/forms';
import { Button } from 'primereact/button';
import TextInputTemplate from '@/Components/Templates/Forms/TextInputTemplate';
import { getFieldConfig, getFormErrorMessage, isFormFieldValid, validateForm } from '@/Helpers/forms';
import NumberInputTemplate from '@/Components/Templates/Forms/NumberInputTemplate';
import { apiRequest, toast } from '@/Helpers/general';
import { AppContext } from '@/Components/Context/AppContext';
import { choFields } from '@/Config/forms/cho';
import DateInputTemplate from '@/Components/Templates/Forms/DateInputTemplate';
import { RadioButton } from 'primereact/radiobutton';

/**
 * Component which renders a modal for adding an alternative date for a B ruleset
 */
const AddDateModal = ({ visible, setVisible, ruleset, caseId, loadRulesets, reloadCaseData }) => {
    const { toastRef } = useContext(AppContext);

    const [formData, setFormData] = useState();
    const [formValidationRules, setFormValidationRules] = useState([]);
    const [formErrors, setFormErrors] = useState({});
    const [formLoading, setFormLoading] = useState(false);
    const [selectedField, setSelectedField] = useState();
    const [showValidationMsg, setShowValidationMsg] = useState(false);

    /**
     * Update the state of the form data (when changing an input value)
     *
     * @param {string} name the name of the input
     * @param {string} value the new value of the input
     */
    const changeFormData = (name, value) => {
        let getFormData = { ...formData };

        getFormData[name] = value;

        setFormData(getFormData);
    };

    /**
     * Generate form field 
     */
    const generateInputField = (field, fieldConfig) => {
        let fieldName = field.relation + '_' + field.field;

        switch (fieldConfig.type) {
            case 'date':
                return (
                    <DateInputTemplate
                        name={fieldName}
                        label={fieldConfig.label}
                        placeholder={fieldsPlaceholders.date}
                        value={formData && formData[fieldName] ? formData[fieldName] : ''}
                        bottomMargin={3}
                        onChangeFunction={changeFormData}
                        isValid={isFormFieldValid(formErrors, fieldName)}
                        errorMessage={getFormErrorMessage(formErrors, fieldName)}
                    />
                );
                break;

            case 'date_range_start':
                return (
                    <DateInputTemplate
                        name={fieldName}
                        label={fieldConfig.label}
                        placeholder={fieldsPlaceholders.dateRange}
                        labelClassName="one-line"
                        value={formData && formData[fieldName] ? formData[fieldName] : ''}
                        bottomMargin={3}
                        onChangeFunction={changeFormData}
                        isValid={isFormFieldValid(formErrors, fieldName)}
                        errorMessage={getFormErrorMessage(formErrors, fieldName)}
                        selectionMode="range"
                    />
                );
                break;

            default:
                break;
        }
    };

    const modalContent = (
        <div className="modal-wrap px-3">
            <div className="flex flex-column align-items-center">
                {ruleset?.extra.map((field) => {
                    let fieldConfig = getFieldConfig(field.relation, field.field);

                    if (fieldConfig) {
                        return (
                            <div className="flex new-date-wrap mb-2" key={ruleset.name + '_' + field.label}>
                                <RadioButton
                                    className="mr-2"
                                    inputId={field.field}
                                    name="selected-date"
                                    value={field}
                                    onChange={(e) => setSelectedField(e.value)}
                                    checked={selectedField && selectedField.field === field.field}
                                />
                                {generateInputField(field, fieldConfig)}
                            </div>
                        );
                    }
                })}
            </div>

            {showValidationMsg && <div className="text-center text-error">Please select a valid date.</div>}
        </div>
    );

    /**
     * Submits the new date
     */
    const submitForm = async () => {
        setFormLoading(true);
        setShowValidationMsg(false);

        if (selectedField && selectedField?.field && selectedField?.relation) {
            let fieldName = selectedField.relation + '_' + selectedField.field,
                selectedDate = formData ? formData[fieldName] : null,
                defaultRules = [{ name: 'required' }],
                fieldConfig = getFieldConfig(selectedField.relation, selectedField.field),
                parseSelectedDate = {};

            let validation = validateForm([{
                field: fieldName,
                rules: fieldConfig?.modalValidation?.length > 0
                    ? fieldConfig.modalValidation.concat(defaultRules)
                    : defaultRules
            }], formData, setFormErrors);

            if (validation && selectedDate) {
                // date ranges
                if (fieldConfig?.type == 'date_range_start') {
                    if (Array.isArray(selectedDate)) {
                        let copyDatesArray = [...selectedDate];

                        if (copyDatesArray[0]) {
                            parseSelectedDate[selectedField.field] = copyDatesArray[0];
                        }

                        if (copyDatesArray[1]) {
                            parseSelectedDate[fieldConfig.endKey] = copyDatesArray[1];
                        }
                    }
                }
                
                apiRequest('post', 'case/' + caseId + '/' + selectedField.relation, {
                    data: fieldConfig?.type == 'date_range_start'
                        ? parseSelectedDate
                        : { [selectedField.field]: selectedDate },
                    onSuccess: (response) => {
                        setFormLoading(false);

                        if (response?.data?.responseType == 'success') {
                            setVisible(false);
                            loadRulesets();
                            reloadCaseData();

                            toast(toastRef, 'success', 'The new date has been saved.');

                        } else {
                            toast(toastRef, 'error', 'Error saving date.');
                        }
                    },
                    onFailure: (error) => {
                        setFormLoading(false);

                        if (error.status == 422) {
                            if (error.messages) {
                                let errorMessages = error.messages,
                                    newFormErrors = {};

                                for (let key in errorMessages) {
                                    if (Object.hasOwnProperty.call(errorMessages, key)) {
                                        newFormErrors[key] = errorMessages[key];
                                    }
                                }

                                // setFormErrors(newFormErrors);

                            } else {
                                toast(toastRef, 'error', 'Error saving date.');
                            }

                        } else {
                            toast(toastRef, 'error', 'Error saving date.');
                        }
                    }
                });

            } else {
                setFormLoading(false);
            }

        } else {
            setFormLoading(false);
            setShowValidationMsg(true);
        }
    };

    const widgetFooter = (
        <>
            <hr />

            <div className="text-right">
                <Button label="Save" onClick={submitForm} loading={formLoading} loadingIcon="pi pi-spin pi-sun" iconPos="center" />
            </div>
        </>
    );

    const resetData = () => {
        setFormData();
        setFormValidationRules([]);
        setFormErrors({});
        setFormLoading(false);
        setSelectedField();
        setShowValidationMsg(false);
    };

    useEffect(() => {
        let initFormData = {};

        // init form data
        ruleset?.extra.forEach((field) => {
            initFormData[field.relation + '_' + field.field] = field.value || '';
        });

        // automatically select a date if only one is available
        if (ruleset?.extra?.length == 1) {
            setSelectedField(ruleset.extra[0]);
        }

        setFormData(initFormData);

    }, [ruleset]);

    return (
        <ConfirmDialogTemplate
            title={'Chose another date'}
            className="add-date-modal"
            message={modalContent}
            onConfirm={() => { }}
            visible={visible}
            setVisible={setVisible}
            onHide={() => {
                setVisible(false);
                resetData();
            }}
            footer={widgetFooter}
            noIcon
        />
    );
};

export default AddDateModal;
